import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_FORM = {
  supplier: '',
  topic: '',
  title: '',
  content: '',
  source_url: '',
  last_checked_at: '',
  language: '',
  tags: '',
};

export default function KnowledgeBase() {
  const { authFetch } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSupplier, setFilterSupplier] = useState('');
  const [filterTopic, setFilterTopic] = useState('');
  const [selected, setSelected] = useState(null); // item being viewed
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // item being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = async (supplier = filterSupplier, topic = filterTopic) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (supplier) params.set('supplier', supplier);
      if (topic) params.set('topic', topic);
      const url = '/api/knowledge' + (params.toString() ? '?' + params.toString() : '');
      const res = await authFetch(url);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { fetchItems(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyFilters = () => {
    fetchItems(filterSupplier, filterTopic);
    setSelected(null);
  };

  const clearFilters = () => {
    setFilterSupplier('');
    setFilterTopic('');
    fetchItems('', '');
    setSelected(null);
  };

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setSelected(null);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      supplier: item.supplier || '',
      topic: item.topic || '',
      title: item.title || '',
      content: item.content || '',
      source_url: item.source_url || '',
      last_checked_at: item.last_checked_at ? item.last_checked_at.slice(0, 10) : '',
      language: item.language || '',
      tags: Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''),
    });
    setShowForm(true);
    setSelected(null);
  };

  const handleSave = async () => {
    if (!form.title || !form.content) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        last_checked_at: form.last_checked_at || null,
      };
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/knowledge/${editing.id}` : '/api/knowledge';
      const res = await authFetch(url, { method, body: JSON.stringify(payload) });
      if (res.ok) {
        setShowForm(false);
        setEditing(null);
        setForm(EMPTY_FORM);
        fetchItems();
      }
    } catch (_) {}
    setSaving(false);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await authFetch(`/api/knowledge/${id}`, { method: 'DELETE' });
      setItems(prev => prev.filter(i => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch (_) {}
    setDeletingId(null);
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 bg-white flex-shrink-0 flex-wrap">
        <button onClick={openCreate} className="btn-primary text-sm">
          + Nieuw item
        </button>
        <input
          className="input text-sm w-44"
          placeholder="Filter op leverancier"
          value={filterSupplier}
          onChange={e => setFilterSupplier(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applyFilters()}
        />
        <input
          className="input text-sm w-44"
          placeholder="Filter op onderwerp"
          value={filterTopic}
          onChange={e => setFilterTopic(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applyFilters()}
        />
        <button onClick={applyFilters} className="btn-secondary text-sm">Filteren</button>
        {(filterSupplier || filterTopic) && (
          <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-gray-700">Wissen</button>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* List */}
        <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-white overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32"><div className="spinner spinner-blue" /></div>
          ) : items.length === 0 ? (
            <div className="p-6 text-center text-gray-400 text-sm">Geen items gevonden</div>
          ) : (
            items.map(item => (
              <div
                key={item.id}
                className={`relative group border-b border-gray-100 ${selected?.id === item.id ? 'bg-blue-50 border-l-2' : ''}`}
                style={selected?.id === item.id ? { borderLeftColor: 'var(--brand)' } : {}}
              >
                <button
                  onClick={() => { setSelected(item); setShowForm(false); }}
                  className="w-full text-left p-4 hover:bg-gray-50 transition-colors"
                >
                  <p className="font-medium text-gray-900 text-sm truncate">{item.title}</p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {item.supplier && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{item.supplier}</span>}
                    {item.topic && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{item.topic}</span>}
                  </div>
                  {item.language && <p className="text-xs text-gray-400 mt-1">{item.language}</p>}
                </button>
                <div className="absolute top-3 right-3 hidden group-hover:flex gap-1">
                  <button
                    onClick={() => openEdit(item)}
                    className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                    title="Bewerken"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    disabled={deletingId === item.id}
                    className="w-7 h-7 rounded flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all disabled:opacity-50"
                    title="Verwijderen"
                  >
                    {deletingId === item.id
                      ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                      : <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    }
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail / Form */}
        <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
          {showForm ? (
            <div className="pro-card p-6 max-w-2xl">
              <h2 className="text-base font-semibold text-gray-900 mb-5">
                {editing ? 'Item bewerken' : 'Nieuw item'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="label text-xs">Titel *</label>
                  <input className="input text-sm" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label text-xs">Leverancier</label>
                    <input className="input text-sm" placeholder="bijv. TUI, Corendon" value={form.supplier} onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label text-xs">Onderwerp</label>
                    <input className="input text-sm" placeholder="bijv. bagage, annulering" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="label text-xs">Inhoud *</label>
                  <textarea className="input text-sm min-h-40 resize-y" value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label text-xs">Taal</label>
                    <input className="input text-sm" placeholder="bijv. nl, en" value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))} />
                  </div>
                  <div>
                    <label className="label text-xs">Laatste controle</label>
                    <input type="date" className="input text-sm" value={form.last_checked_at} onChange={e => setForm(f => ({ ...f, last_checked_at: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <label className="label text-xs">Tags (kommagescheiden)</label>
                  <input className="input text-sm" placeholder="bijv. vliegtuig, handbagage" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
                </div>
                <div>
                  <label className="label text-xs">Bron URL</label>
                  <input className="input text-sm" placeholder="https://..." value={form.source_url} onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))} />
                </div>
              </div>
              <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                <button onClick={handleSave} disabled={saving || !form.title || !form.content} className="btn-primary text-sm">
                  {saving ? <span className="spinner" /> : null}
                  {saving ? 'Opslaan...' : 'Opslaan'}
                </button>
                <button onClick={() => { setShowForm(false); setEditing(null); }} className="btn-secondary text-sm">Annuleren</button>
              </div>
            </div>
          ) : selected ? (
            <div className="pro-card p-6 max-w-2xl">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900">{selected.title}</h2>
                <button onClick={() => openEdit(selected)} className="btn-secondary text-xs">Bewerken</button>
              </div>
              <div className="flex gap-2 flex-wrap mb-4">
                {selected.supplier && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">{selected.supplier}</span>}
                {selected.topic && <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{selected.topic}</span>}
                {selected.language && <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded">{selected.language}</span>}
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 leading-relaxed border border-gray-100 whitespace-pre-wrap mb-4">
                {selected.content}
              </div>
              <div className="space-y-1 text-xs text-gray-400">
                {selected.source_url && <p>Bron: <a href={selected.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{selected.source_url}</a></p>}
                {selected.last_checked_at && <p>Laatste controle: {new Date(selected.last_checked_at).toLocaleDateString('nl-NL')}</p>}
                {selected.tags && (Array.isArray(selected.tags) ? selected.tags : [selected.tags]).filter(Boolean).length > 0 && (
                  <p>Tags: {(Array.isArray(selected.tags) ? selected.tags : [selected.tags]).join(', ')}</p>
                )}
                <p>Aangemaakt: {new Date(selected.created_at).toLocaleDateString('nl-NL')}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="text-sm font-medium text-gray-700">Selecteer een item</h3>
                <p className="text-xs text-gray-400 mt-1">Of maak een nieuw item aan</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
